import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import { NgPrincipleSharedModule } from '@principle-theorem/ng-principle-shared';
import { IBreadcrumb, NgSharedModule } from '@principle-theorem/ng-shared';
import { BigQueryUploadJob } from '@principle-theorem/principle-core';
import { all$, Firestore, MILLIS_IN_SECOND } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { duration } from 'moment-timezone';
import { interval, Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { BigQueryUploadToolbarComponent } from './big-query-upload-toolbar/big-query-upload-toolbar.component';
import { BigQueryUploadStore } from './big-query-upload.store';

@Component({
  selector: 'pr-big-query-uploads',
  templateUrl: './big-query-uploads.component.html',
  styleUrls: ['./big-query-uploads.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgMaterialModule,
    NgSharedModule,
    NgPrincipleSharedModule,
    BigQueryUploadToolbarComponent,
  ],
  providers: [BigQueryUploadStore],
})
export class BigQueryUploadsComponent {
  store = inject(BigQueryUploadStore);

  breadcrumbs: IBreadcrumb[] = [{ label: 'Tools', path: '../' }];
  tabs: IBreadcrumb[] = [
    { label: 'Bucket Monitoring', path: './stats' },
    { label: 'Jobs', path: './jobs' },
  ];
  recentDuration = duration(10, 'minutes');

  constructor() {
    const fromDate$ = this._timeTicker$(30).pipe(
      map((now) => now.subtract(this.recentDuration))
    );

    Firestore.safeDoc$(BigQueryUploadJob.config())
      .pipe(takeUntilDestroyed())
      .subscribe((config) => this.store.setConfig(config));

    fromDate$
      .pipe(
        switchMap((fromDate) =>
          all$(BigQueryUploadJob.queryRecentStats(fromDate))
        ),
        takeUntilDestroyed()
      )
      .subscribe((stats) => this.store.setStats(stats));

    fromDate$
      .pipe(
        switchMap((fromDate) =>
          all$(BigQueryUploadJob.queryRecentJobs(fromDate))
        ),
        takeUntilDestroyed()
      )
      .subscribe((jobs) => this.store.setJobs(jobs));
  }

  private _timeTicker$(seconds: number): Observable<moment.Moment> {
    return interval(seconds * MILLIS_IN_SECOND).pipe(
      startWith(0),
      map(() => moment())
    );
  }
}
